import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { OutcomeBadge, FEATURE_LABELS } from '@/components/testlab/shared';

const SPLITS = [['all', 'Alla'], ['train', 'Träning'], ['validation', 'Validering'], ['test', 'Test']];

function Metric({ label, m, testid }) {
  return (
    <Card><CardContent className="p-3" data-testid={testid}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-bold">{m ? `${String(m.value).replace('.', ',')} %` : '—'}</p>
      <p className="text-[10px] text-muted-foreground">{m ? `${m.k} av ${m.n} · 95 % KI ${m.ci_low}–${m.ci_high} %` : 'Inget underlag'}</p>
    </CardContent></Card>
  );
}

export function ValidationMetrics({ api, onOpen }) {
  const [split, setSplit] = useState('all');
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api(`outcomes${split !== 'all' ? `?split=${split}` : ''}`).then(setD).catch(e => toast.error(e.message)); }, [api, split]);
  const m = d?.metrics;
  return (
    <div className="space-y-3" data-testid="validation-metrics">
      <div className="flex gap-1 flex-wrap">{SPLITS.map(([k, l]) => (
        <Button key={k} size="sm" className="h-7 text-xs" variant={split === k ? 'default' : 'outline'} onClick={() => setSplit(k)} data-testid={`metrics-split-${k}`}>{l}</Button>
      ))}</div>
      {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : <>
        <p className="text-[11px] text-muted-foreground">{d.definition}</p>
        {m.warnings.map(w => <p key={w} className="text-xs text-amber-700 flex items-center gap-1" data-testid="metrics-warning"><AlertTriangle className="h-3.5 w-3.5" />{w}</p>)}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Metric label="Sensitivitet (recall)" m={m.sensitivity} testid="metric-sensitivity" />
          <Metric label="Specificitet" m={m.specificity} testid="metric-specificity" />
          <Metric label="Precision" m={m.precision} testid="metric-precision" />
          <Metric label="Falsk positiv-frekvens" m={m.false_positive_rate} testid="metric-fpr" />
          <Metric label="Falsk negativ-frekvens" m={m.false_negative_rate} testid="metric-fnr" />
        </div>
        <p className="text-xs">TP {m.counts.TP} · FP {m.counts.FP} · TN {m.counts.TN} · FN {m.counts.FN} · Blandade: {m.mixed.detected} vape / {m.mixed.not_detected} ej vape · Otillräckliga data: {m.insufficient_data_tests} test · Snitt tid till detektion (TP): {m.mean_seconds_to_detection != null ? `ca ${m.mean_seconds_to_detection} s (n=${m.detection_time_n})` : '—'}</p>
        <div className="overflow-x-auto rounded-lg border"><table className="w-full text-xs" data-testid="metrics-per-category">
          <thead className="bg-muted/50 text-left"><tr><th className="px-2 py-1.5">Facit</th><th className="px-2 py-1.5">Tester</th><th className="px-2 py-1.5">Med beslut</th><th className="px-2 py-1.5">VAPE-beslut</th><th className="px-2 py-1.5">Underlag</th></tr></thead>
          <tbody>{Object.entries(d.per_category).map(([c, v]) => (
            <tr key={c} className="border-t"><td className="px-2 py-1.5 font-semibold">{c}</td><td className="px-2 py-1.5">{v.tests}</td><td className="px-2 py-1.5">{v.evaluable}</td>
              <td className="px-2 py-1.5">{v.vape_decisions ? `${v.vape_decisions.k} (${String(v.vape_decisions.value).replace('.', ',')} %)` : '—'}</td>
              <td className="px-2 py-1.5">{v.insufficient ? <span className="text-amber-700">Otillräckligt</span> : 'OK'}</td></tr>
          ))}</tbody>
        </table></div>
        <div className="space-y-1">{d.rows.filter(r => r.outcome !== 'INSUFFICIENT_DATA').map(r => (
          <button key={r.id} onClick={() => onOpen(r.id)} className="w-full flex items-center gap-2 text-xs border rounded px-2 py-1.5 hover:bg-muted/30 text-left" data-testid={`outcome-row-${r.id}`}>
            <OutcomeBadge outcome={r.outcome} /><span className="flex-1">{r.name}</span><span className="text-muted-foreground">{r.split}</span>
          </button>
        ))}</div>
      </>}
    </div>
  );
}

export function SignalAnalysis({ api }) {
  const [d, setD] = useState(null);
  useEffect(() => { api('features').then(setD).catch(e => toast.error(e.message)); }, [api]);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin" />;
  const cats = Object.keys(d.categories);
  return (
    <div className="space-y-2" data-testid="signal-analysis">
      <p className="text-[11px] text-muted-foreground">{d.note} Median (kvartil 1–3) per facitkategori. Färre än {d.min_per_category} tester = otillräckligt underlag.</p>
      {!cats.length ? <p className="text-sm text-muted-foreground">Inga tester med mätdata ännu.</p> : (
        <div className="overflow-x-auto rounded-lg border"><table className="w-full text-xs">
          <thead className="bg-muted/50 text-left"><tr><th className="px-2 py-1.5">Signal</th>{cats.map(c => (
            <th key={c} className="px-2 py-1.5">{c} <span className="font-normal text-muted-foreground">(n={d.categories[c].tests})</span>{d.categories[c].insufficient && <Badge variant="outline" className="ml-1 text-[9px] text-amber-700">otillräckligt</Badge>}</th>
          ))}</tr></thead>
          <tbody>{Object.entries(FEATURE_LABELS).map(([k, l]) => (
            <tr key={k} className="border-t"><td className="px-2 py-1.5 font-medium">{l}</td>{cats.map(c => {
              const f = d.categories[c].features[k];
              return <td key={c} className="px-2 py-1.5 font-mono">{f ? `${f.median} (${f.q1}–${f.q3})` : '—'}</td>;
            })}</tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}

export function SplitGuard({ api }) {
  const [d, setD] = useState(null);
  const load = () => api('splits').then(setD).catch(e => toast.error(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const lock = async () => {
    try { const r = await api('splits/lock', { method: 'POST' }); toast.success(`${r.locked_now} tester låsta`); load(); } catch (e) { toast.error(e.message); }
  };
  if (!d) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <div className="space-y-3" data-testid="split-guard">
      <div className="flex items-center gap-3">
        <p className="text-xs"><Lock className="inline h-3.5 w-3.5 mr-1" />{d.locked} av {d.total} tester har låst grupp.</p>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={lock} data-testid="lock-splits-btn">Lås ej låsta tester</Button>
      </div>
      <div className="grid md:grid-cols-3 gap-2">{Object.entries(d.composition).map(([k, comp]) => (
        <Card key={k}><CardContent className="p-3" data-testid={`split-card-${k}`}>
          <p className="font-semibold text-sm">{{ train: 'Träning', validation: 'Validering', test: 'Slutlig test' }[k]}</p>
          <p className="text-xs text-muted-foreground">{Object.entries(comp).map(([c, n]) => `${c}: ${n}`).join(' · ') || 'Inga tester'}</p>
          {d.missing[k].length > 0 && <p className="text-[11px] text-amber-700 mt-1">Saknar: {d.missing[k].join(', ')}</p>}
        </CardContent></Card>
      ))}</div>
      <p className="text-[11px] text-muted-foreground">Låsta grupper ändras aldrig automatiskt. Slutlig testgrupp får inte användas för att utveckla eller justera algoritmen.</p>
    </div>
  );
}

export function Experiments({ api }) {
  const [d, setD] = useState(null);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api('experiments').then(setD).catch(e => toast.error(e.message)); }, [api]);
  const start = async (algorithm, split) => {
    setBusy(true);
    try { setRun(await api('experiments/run', { method: 'POST', body: JSON.stringify({ algorithm, split }) })); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (!d) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <div className="space-y-3" data-testid="experiments">
      <p className="text-[11px] text-muted-foreground">Experimentella algoritmer körs offline på sparade mätserier och påverkar aldrig produktionens beslut eller larm. Testgruppen är spärrad.</p>
      {d.algorithms.map(a => (
        <Card key={a.id}><CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px]"><p className="font-mono text-xs font-semibold">{a.id}</p><p className="text-xs text-muted-foreground">{a.description}</p></div>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => start(a.id, 'train')} data-testid={`exp-run-train-${a.id}`}>Kör på träning</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => start(a.id, 'validation')} data-testid={`exp-run-validation-${a.id}`}>Kör på validering</Button>
        </CardContent></Card>
      ))}
      {run && (
        <Card><CardContent className="p-3 text-xs space-y-1" data-testid="experiment-result">
          <p className="font-semibold">{run.algorithm} på {run.split} ({run.tests} tester) – EXPERIMENTELLT</p>
          {[['Produktion', run.production_metrics], ['Experimentell', run.experimental_metrics]].map(([l, m]) => (
            <p key={l}>{l}: TP {m.counts.TP} · FP {m.counts.FP} · TN {m.counts.TN} · FN {m.counts.FN}{m.insufficient ? ' · otillräckligt underlag' : ''}</p>
          ))}
        </CardContent></Card>
      )}
    </div>
  );
}
